import { useTranslation } from "react-i18next";
import { Airplane, IdentificationCard, Sun, CloudSnow, Umbrella, Info } from "@phosphor-icons/react";

/**
 * TravelSidebar — curated country hints for out-of-region researchers.
 *
 * Editorial choice: we ship static, curated hints (not live API data) because:
 *   1. Visa + climate rules change slowly — a snapshot is safer than a stale
 *      or throttled 3rd-party call the day someone books a flight.
 *   2. Flight cost bands are order-of-magnitude signals, not quotes.
 * We always show the "verify with your embassy / carrier" disclaimer so the
 * hint stays advisory. Countries we don't cover fall back to a generic block.
 */

// Season buckets by month index (0=Jan). MENA weather notes are tuned to the
// dominant hub city (Riyadh/Dubai/Doha/etc.). Winter = mild/warm, Summer = hot.
const SEASON = {
  winter: { icon: CloudSnow, label: "Mild winter" },
  spring: { icon: Sun, label: "Spring" },
  summer: { icon: Sun, label: "Hot summer" },
  fall: { icon: Umbrella, label: "Fall" },
};

const COUNTRIES = {
  "Saudi Arabia": {
    visa: "eVisa online for 60+ nationalities via visa.visitsaudi.com. GCC citizens: no visa.",
    climate: {
      winter: "Pleasant 15–24°C (Riyadh). Ideal for outdoor sessions.",
      spring: "Warm 25–32°C. Dust storms possible in March.",
      summer: "Extreme 40–48°C. Indoor venues + hydration essential.",
      fall: "Cooling to 25–35°C.",
    },
    flights: {
      us: "$900–$1,600 to RUH/JED",
      eu: "$450–$850 to RUH/JED",
      asia: "$400–$800 to RUH/JED",
    },
    currency: "SAR (pegged to USD ~3.75)",
  },
  "United Arab Emirates": {
    visa: "Visa on arrival for 60+ nationalities (30–90 days). eVisa via smartservices.icp.gov.ae for others.",
    climate: {
      winter: "Perfect 18–26°C. Peak tourist season.",
      spring: "Warm 24–34°C. Humidity rising in May.",
      summer: "Intense 38–45°C + 80% humidity. Indoor only.",
      fall: "Comfortable 26–35°C from mid-October.",
    },
    flights: { us: "$800–$1,500 to DXB/AUH", eu: "$400–$800 to DXB/AUH", asia: "$300–$700 to DXB/AUH" },
    currency: "AED (~3.67 = 1 USD)",
  },
  "Qatar": {
    visa: "Free visa on arrival (30 days) for 95+ nationalities. Hayya card required for events.",
    climate: {
      winter: "Mild 15–24°C. Best season.",
      spring: "Warm 22–33°C.",
      summer: "Extreme 38–45°C. Humid coast.",
      fall: "Cooling 24–34°C.",
    },
    flights: { us: "$850–$1,550 to DOH", eu: "$450–$800 to DOH", asia: "$350–$700 to DOH" },
    currency: "QAR (pegged ~3.64 = 1 USD)",
  },
  "Bahrain": {
    visa: "eVisa via evisa.gov.bh for 100+ nationalities. GCC citizens: no visa.",
    climate: { winter: "Mild 14–21°C.", spring: "Warm 20–32°C.", summer: "Hot 33–41°C + humid.", fall: "23–33°C." },
    flights: { us: "$900–$1,600 to BAH", eu: "$500–$850 to BAH", asia: "$400–$750 to BAH" },
    currency: "BHD (~0.377 = 1 USD)",
  },
  "Kuwait": {
    visa: "eVisa via evisa.moi.gov.kw. GCC citizens: no visa.",
    climate: { winter: "Cool 8–19°C.", spring: "Mild 18–32°C. Frequent dust.", summer: "Extreme 42–50°C.", fall: "20–35°C." },
    flights: { us: "$900–$1,600 to KWI", eu: "$500–$850 to KWI", asia: "$400–$750 to KWI" },
    currency: "KWD (~0.31 = 1 USD, highest-value currency)",
  },
  "Oman": {
    visa: "eVisa via evisa.rop.gov.om. GCC citizens: no visa.",
    climate: { winter: "Mild 17–26°C. Best travel window.", spring: "Warm 22–35°C.", summer: "Hot 35–44°C. Salalah cool via khareef monsoon.", fall: "25–34°C." },
    flights: { us: "$950–$1,700 to MCT", eu: "$500–$900 to MCT", asia: "$400–$800 to MCT" },
    currency: "OMR (~0.385 = 1 USD)",
  },
  "Morocco": {
    visa: "Visa-free (90 days) for US/EU/UK/Canada/AU/many others. eVisa for select nationalities.",
    climate: {
      winter: "Cool 8–18°C (Rabat/Casablanca). Marrakech mild.",
      spring: "Perfect 15–26°C. Peak season.",
      summer: "Warm coast 20–30°C, hot inland Marrakech 35–42°C.",
      fall: "Ideal 18–28°C.",
    },
    flights: { us: "$500–$1,000 to CMN/RAK", eu: "$150–$400 to CMN/RAK", asia: "$700–$1,300 to CMN/RAK" },
    currency: "MAD (~10 = 1 USD)",
  },
  "Tunisia": {
    visa: "Visa-free (90 days) for US/EU/UK/Canada/most Africa.",
    climate: { winter: "Cool 8–17°C.", spring: "Pleasant 14–24°C.", summer: "Hot 22–35°C, dry inland.", fall: "18–28°C." },
    flights: { us: "$700–$1,300 to TUN", eu: "$150–$450 to TUN", asia: "$800–$1,400 to TUN" },
    currency: "TND (~3.1 = 1 USD)",
  },
  "Jordan": {
    visa: "Visa on arrival ($56) or free with Jordan Pass (buy online before arrival — bundles Petra + waives visa fee).",
    climate: {
      winter: "Cool 5–14°C (Amman). Snow possible.",
      spring: "Ideal 15–25°C.",
      summer: "Hot dry 25–35°C. Dead Sea +5°C.",
      fall: "Mild 18–28°C.",
    },
    flights: { us: "$750–$1,400 to AMM", eu: "$300–$650 to AMM", asia: "$450–$850 to AMM" },
    currency: "JOD (~0.71 = 1 USD)",
  },
  "Lebanon": {
    visa: "Free visa on arrival (30 days) for most nationalities including US/EU/UK. Passport must be Israel-stamp-free.",
    climate: {
      winter: "Mild coast 8–16°C. Snow in mountains.",
      spring: "Pleasant 14–24°C.",
      summer: "Warm 22–32°C. Humid coast.",
      fall: "Comfortable 18–28°C.",
    },
    flights: { us: "$700–$1,400 to BEY", eu: "$300–$650 to BEY", asia: "$450–$900 to BEY" },
    currency: "LBP (volatile — most transactions in USD)",
  },
  "Algeria": {
    visa: "Visa required in advance for most nationalities. Business/conference invitation letter required.",
    climate: { winter: "Cool 5–15°C (Algiers).", spring: "Mild 13–22°C.", summer: "Warm coast 22–32°C, hot Sahara 40°C+.", fall: "17–26°C." },
    flights: { us: "$900–$1,500 to ALG", eu: "$200–$500 to ALG", asia: "$800–$1,400 to ALG" },
    currency: "DZD (~135 = 1 USD)",
  },
  "Iraq": {
    visa: "Visa on arrival for 37 nationalities (Baghdad, Erbil airports). Erbil (Kurdistan) has separate friendlier policy.",
    climate: { winter: "Cool 5–15°C (Baghdad).", spring: "Warm 15–30°C. Dust common.", summer: "Extreme 35–48°C.", fall: "20–33°C." },
    flights: { us: "$900–$1,600 to BGW/EBL", eu: "$400–$800 to BGW/EBL", asia: "$500–$1,000 to BGW/EBL" },
    currency: "IQD (~1,310 = 1 USD)",
  },
  "Egypt": {
    visa: "eVisa via visa2egypt.gov.eg or visa on arrival ($25). Free for many African + GCC.",
    climate: { winter: "Mild 10–20°C (Cairo).", spring: "Warm 15–30°C. Khamaseen wind March–May.", summer: "Hot 25–40°C.", fall: "20–32°C." },
    flights: { us: "$700–$1,300 to CAI", eu: "$200–$500 to CAI", asia: "$400–$900 to CAI" },
    currency: "EGP (~50 = 1 USD)",
  },
  "Palestine": {
    visa: "Enter via Israel (Ben Gurion or Allenby land bridge from Jordan). No separate Palestinian visa. Confirm current access with organizers.",
    climate: { winter: "Cool 8–16°C (Ramallah).", spring: "Pleasant 14–24°C.", summer: "Warm dry 22–32°C.", fall: "18–28°C." },
    flights: { us: "$800–$1,500 to TLV or AMM (+land transfer)", eu: "$300–$700 to TLV or AMM", asia: "$500–$1,000 to TLV or AMM" },
    currency: "ILS (~3.7 = 1 USD)",
  },
  "United States": {
    visa: "ESTA online for 40+ visa-waiver nationalities (must apply ≥72h before travel). B1/B2 conference visa otherwise.",
    climate: { winter: "Varies widely by state.", spring: "Mild.", summer: "Warm to hot.", fall: "Mild." },
    flights: { us: "Domestic $150–$600", eu: "$400–$900", asia: "$800–$1,600" },
    currency: "USD",
  },
  "United Kingdom": {
    visa: "Standard Visitor visa or ETA for conferences. US/EU/AU/CA: ETA £10 online.",
    climate: { winter: "Cold 2–8°C.", spring: "Cool 6–15°C.", summer: "Mild 15–24°C.", fall: "Cool 8–15°C." },
    flights: { us: "$400–$900 to LHR", eu: "$60–$250 to LHR", asia: "$500–$1,100 to LHR" },
    currency: "GBP (~0.79 = 1 USD)",
  },
};

const GENERIC = {
  visa: "Verify visa requirements with the destination's embassy in advance — most conferences can provide an invitation letter on request.",
  climate: {
    winter: "Check local seasonal norms.",
    spring: "Check local seasonal norms.",
    summer: "Check local seasonal norms.",
    fall: "Check local seasonal norms.",
  },
  flights: { us: "Varies", eu: "Varies", asia: "Varies" },
  currency: "Check local currency + exchange rate at time of booking.",
};

// Normalize looser inputs (case, whitespace, common aliases) to canonical
// country keys so seed variations don't silently drop to GENERIC.
const ALIASES = {
  "uae": "United Arab Emirates",
  "u.a.e.": "United Arab Emirates",
  "emirates": "United Arab Emirates",
  "ksa": "Saudi Arabia",
  "kingdom of saudi arabia": "Saudi Arabia",
  "usa": "United States",
  "u.s.": "United States",
  "u.s.a.": "United States",
  "us": "United States",
  "america": "United States",
  "uk": "United Kingdom",
  "u.k.": "United Kingdom",
  "britain": "United Kingdom",
  "great britain": "United Kingdom",
  "england": "United Kingdom",
};

function resolveCountry(raw) {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (COUNTRIES[trimmed]) return COUNTRIES[trimmed];
  const alias = ALIASES[trimmed.toLowerCase()];
  if (alias && COUNTRIES[alias]) return COUNTRIES[alias];
  // Also try lowercased exact match against the canonical keys.
  const lc = trimmed.toLowerCase();
  for (const k of Object.keys(COUNTRIES)) {
    if (k.toLowerCase() === lc) return COUNTRIES[k];
  }
  return null;
}

function seasonOf(dateStr) {
  if (!dateStr) return "spring";
  try {
    const m = new Date(dateStr).getMonth(); // 0-11
    if (m === 11 || m <= 1) return "winter";
    if (m >= 2 && m <= 4) return "spring";
    if (m >= 5 && m <= 8) return "summer";
    return "fall";
  } catch {
    return "spring";
  }
}

export default function TravelSidebar({ conf }) {
  const { t } = useTranslation();
  if (!conf?.country) return null;

  const data = resolveCountry(conf.country) || GENERIC;
  const season = seasonOf(conf.start_date);
  const SeasonIcon = SEASON[season].icon;

  return (
    <div className="border-2 border-zinc-950 bg-white dark:bg-zinc-900 dark:border-zinc-100 p-6 mt-3" data-testid="travel-sidebar">
      <div className="flex items-center gap-2 mb-4">
        <Airplane size={16} weight="duotone" className="text-blue-700" />
        <div className="overline">{t("travel.title") || "Travel notes"}</div>
      </div>

      {/* Visa */}
      <div className="border-b border-zinc-200 dark:border-zinc-800 pb-4 mb-4">
        <div className="flex items-center gap-1.5 mb-2">
          <IdentificationCard size={12} className="text-zinc-500" />
          <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500">{t("travel.visa") || "Visa"}</div>
        </div>
        <p className="text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed" data-testid="travel-visa">{data.visa}</p>
      </div>

      {/* Climate */}
      <div className="border-b border-zinc-200 dark:border-zinc-800 pb-4 mb-4">
        <div className="flex items-center gap-1.5 mb-2">
          <SeasonIcon size={12} className="text-zinc-500" />
          <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500">
            {t("travel.climate") || "Climate"} · {SEASON[season].label}
          </div>
        </div>
        <p className="text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed" data-testid="travel-climate">{data.climate[season]}</p>
      </div>

      {/* Flights */}
      <div className="border-b border-zinc-200 dark:border-zinc-800 pb-4 mb-4">
        <div className="flex items-center gap-1.5 mb-2">
          <Airplane size={12} className="text-zinc-500" />
          <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500">{t("travel.flights") || "Flights (economy, indicative)"}</div>
        </div>
        <div className="space-y-1 font-mono text-xs text-zinc-600 dark:text-zinc-400" data-testid="travel-flights">
          <div className="flex justify-between"><span>from US</span><span className="text-zinc-950 dark:text-zinc-100">{data.flights.us}</span></div>
          <div className="flex justify-between"><span>from EU</span><span className="text-zinc-950 dark:text-zinc-100">{data.flights.eu}</span></div>
          <div className="flex justify-between"><span>from Asia</span><span className="text-zinc-950 dark:text-zinc-100">{data.flights.asia}</span></div>
        </div>
      </div>

      {/* Currency */}
      <div className="mb-4">
        <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 mb-1">{t("travel.currency") || "Currency"}</div>
        <p className="text-sm text-zinc-700 dark:text-zinc-300" data-testid="travel-currency">{data.currency}</p>
      </div>

      {/* Disclaimer */}
      <div className="flex gap-1.5 text-[11px] font-mono text-zinc-500 leading-relaxed pt-3 border-t border-zinc-100 dark:border-zinc-800">
        <Info size={11} className="mt-0.5 flex-shrink-0" />
        <span>{t("travel.disclaimer") || "Estimates only. Verify with your embassy and carrier before booking."}</span>
      </div>
    </div>
  );
}
